import React from "react";
import { Captions, Check, ChevronRight, Eye, EyeOff, Plus } from "lucide-react";
import Checkbox from "../../ui/Checkbox";
import Tooltip from "../../ui/Tooltip";
import { SCENE_GRAPH_NODE_COLORS } from "../../../utils/sceneGraphNodeAppearance";

function SceneInventoryTargetRow({
  row,
  target,
  targetIndex,
  activeTargetId,
  selectedBodies,
  selectedMeshIds,
  getItemHidden,
  onOpenTarget,
  onTargetRowKeyDown,
  onToggleItemVisibility,
  onToggleMultiSelection,
}) {
  const isSelected = target.id === activeTargetId;
  const isInMultiSelection =
    row.key === "body"
      ? selectedBodies.includes(target.id)
      : row.key === "mesh"
        ? selectedMeshIds.has(target.id)
        : false;
  const isHighlighted = isSelected || isInMultiSelection;
  const isHidden = row.hasVisibility ? getItemHidden(row.key, target.id) : false;
  const handleMultiSelectGesture = React.useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      onToggleMultiSelection(row.key, target.id, row.targets, event);
    },
    [onToggleMultiSelection, row.key, row.targets, target.id]
  );
  const handleRowClick = React.useCallback(
    (event) => {
      if (row.supportsMultiSelect && (event.shiftKey || event.ctrlKey || event.metaKey)) {
        handleMultiSelectGesture(event);
        return;
      }
      onOpenTarget(row.key, target);
    },
    [handleMultiSelectGesture, onOpenTarget, row.key, row.supportsMultiSelect, target]
  );
  const handleRowKeyDown = React.useCallback(
    (event) => onTargetRowKeyDown(row.key, row.targets, targetIndex, event),
    [onTargetRowKeyDown, row.key, row.targets, targetIndex]
  );

  return (
    <div
      className={`flex w-full min-w-0 items-center gap-1 rounded-md transition-colors ${
        isHighlighted
          ? "bg-[rgba(80,80,80,0.65)] text-gray-100"
          : "text-gray-300 hover:bg-[rgba(80,80,80,0.45)] hover:text-gray-100"
      }`}
    >
      {row.supportsMultiSelect && (
        <Checkbox
          checked={isInMultiSelection}
          onChange={(event) => {
            event.stopPropagation();
          }}
          onClick={handleMultiSelectGesture}
          onKeyDown={handleRowKeyDown}
          title={isInMultiSelection ? "Remove from selection" : "Add to selection"}
          aria-label={isInMultiSelection ? "Remove from selection" : "Add to selection"}
          className="shrink-0 px-0 py-0 [&:focus-within]:ring-2 [&:focus-within]:ring-gray-500 [&:focus-within]:ring-offset-0 [&:focus-within]:ring-offset-transparent rounded"
        />
      )}
      <button
        type="button"
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        data-scene-inventory-item="true"
        className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2 py-1.5 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          {!row.supportsMultiSelect &&
            (isSelected ? (
              <Check className="h-3 w-3 shrink-0 text-white" />
            ) : (
              <span className="h-3 w-3 shrink-0" />
            ))}
          <span className="min-w-0">
            <span className="block min-w-0 truncate text-xs">
              <span className="font-medium">{target.label}</span>
              {target.detail ? (
                <span className={isHighlighted ? "text-gray-300" : "panel-text-helper"}>
                  {" · "}
                  {target.detail}
                </span>
              ) : null}
            </span>
          </span>
        </span>
        {isSelected ? (
          <span className="shrink-0 text-xs font-medium text-gray-300">Selected</span>
        ) : null}
      </button>
      {row.hasVisibility && (
        <Tooltip content={isHidden ? "Show in 3D scene" : "Hide in 3D scene"}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleItemVisibility(row.key, target.id, isHidden);
            }}
            className={`mr-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors hover:bg-gray-700/70 disabled:cursor-not-allowed disabled:text-gray-600 disabled:hover:bg-transparent ${
              isHidden ? "text-white hover:text-white" : "text-gray-400 hover:text-gray-100"
            }`}
            aria-label={isHidden ? "Show in 3D scene" : "Hide in 3D scene"}
          >
            {isHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </Tooltip>
      )}
    </div>
  );
}

function getLayerTypeName(row) {
  if (row.key === "object" || row.key === "body") return "bodies";
  if (row.key === "mesh" || row.key === "visual") return "visuals";
  if (row.key === "constraint") return "constraints";
  if (row.key === "tendon") return "tendons";
  return row.label.toLowerCase();
}

export default function SceneInventorySection({
  row,
  isExpanded,
  activeTargetId,
  activeTarget,
  hiddenCount,
  labelToggleActive,
  typeVisible,
  quickAddDisabledReason,
  selectedCount,
  selectedBodies,
  selectedMeshIds,
  getItemHidden,
  onToggleExpanded,
  onQuickAdd,
  onToggleTypeVisibility,
  onToggleTypeLabels,
  onToggleItemVisibility,
  onClearMultiSelection,
  onSelectAllTargets,
  onToggleMultiSelection,
  onOpenTarget,
  onTargetRowKeyDown,
}) {
  const count = row.targets.length;
  const disabled = count === 0;
  const nodeAppearance = SCENE_GRAPH_NODE_COLORS[row.graphType];
  const RowIcon = row.Icon;
  const labelToggleTitle =
    labelToggleActive == null
      ? ""
      : `${labelToggleActive ? "Hide" : "Show"} all ${row.label.toLowerCase()} labels`;
  const typeVisibilityTitle =
    typeVisible == null
      ? ""
      : `${typeVisible ? "Hide" : "Show"} all ${getLayerTypeName(row)} in 3D scene`;
  const quickAddTitle = quickAddDisabledReason || `Add ${row.label.toLowerCase()}`;
  const showMultiSelectionActions = row.supportsMultiSelect && selectedCount > 1;
  const allTargetsSelected = showMultiSelectionActions && selectedCount >= count;

  return (
    <div className="rounded-lg border border-gray-800/80 bg-gray-950/30">
      <div
        className={`group flex w-full items-stretch rounded-lg transition-colors ${
          disabled
            ? "panel-text-helper"
            : activeTarget
              ? "bg-[rgba(80,80,80,0.65)] text-gray-100"
              : "text-gray-200 hover:bg-[rgba(80,80,80,0.45)]"
        }`}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => onToggleExpanded(row.key)}
          className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left disabled:cursor-not-allowed"
          aria-expanded={isExpanded}
        >
          <ChevronRight
            className="h-4 w-4 shrink-0 text-gray-400 transition-transform"
            style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
          />
          <div
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor: nodeAppearance.base,
              border: `1.5px solid ${nodeAppearance.stroke}`,
            }}
          >
            <RowIcon className={`${row.iconCls} text-white`} />
          </div>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-gray-100">{row.label}</span>
            <span className="block truncate text-xs text-gray-400">
              {row.supportsMultiSelect && selectedCount > 1
                ? `${selectedCount} selected`
                : activeTarget
                  ? activeTarget.label
                  : ""}
            </span>
          </span>
          <span className="shrink-0 rounded-full border border-gray-400 px-2 py-0.5 text-xs text-gray-200">
            {count}
          </span>
          {hiddenCount > 0 ? (
            <Tooltip content={`${hiddenCount} hidden in 3D`}>
              <span
                className="inline-flex shrink-0 items-center gap-1 text-xs tabular-nums text-gray-400"
                aria-label={`${hiddenCount} hidden in 3D scene`}
              >
                <EyeOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span aria-hidden>{hiddenCount}</span>
              </span>
            </Tooltip>
          ) : null}
        </button>
        {typeVisible != null || labelToggleActive != null ? (
          <div className="flex shrink-0 items-center gap-1 border-l border-gray-800 px-2">
            <Tooltip content={quickAddTitle} side="top">
              <button
                type="button"
                disabled={!!quickAddDisabledReason}
                onClick={(event) => {
                  event.stopPropagation();
                  onQuickAdd(row.key);
                }}
                className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-700/70 hover:text-gray-100 disabled:cursor-not-allowed disabled:text-gray-600 disabled:hover:bg-transparent"
                aria-label={quickAddTitle}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
            {typeVisible != null ? (
              <Tooltip content={typeVisibilityTitle} side="top">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleTypeVisibility(row.key);
                  }}
                  className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:text-gray-600 disabled:hover:bg-transparent ${
                    typeVisible
                      ? "bg-gray-700/80 text-white hover:bg-gray-600/90"
                      : "text-gray-400 hover:bg-gray-700/70 hover:text-gray-100"
                  }`}
                  aria-label={typeVisibilityTitle}
                  aria-pressed={typeVisible}
                >
                  {typeVisible ? (
                    <Eye className="h-3.5 w-3.5" />
                  ) : (
                    <EyeOff className="h-3.5 w-3.5" />
                  )}
                </button>
              </Tooltip>
            ) : null}
            {labelToggleActive != null ? (
              <Tooltip content={labelToggleTitle} side="top">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleTypeLabels(row.key);
                  }}
                  className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:text-gray-600 disabled:hover:bg-transparent ${
                    labelToggleActive
                      ? "bg-gray-700/80 text-white hover:bg-gray-600/90"
                      : "text-gray-400 hover:bg-gray-700/70 hover:text-gray-100"
                  }`}
                  aria-label={labelToggleTitle}
                  aria-pressed={labelToggleActive}
                >
                  <Captions className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>
      {isExpanded ? (
        <div className="border-t border-gray-800/80 px-2 py-1.5">
          <div
            className="space-y-0.5 border-l border-gray-700/70 pl-2"
            data-scene-inventory-list="true"
          >
            {showMultiSelectionActions ? (
              <div className="mb-1.5 flex items-center justify-between gap-2 rounded-md bg-gray-900/70 px-2 py-1.5">
                <span className="min-w-0 truncate text-xs text-gray-400">
                  {selectedCount} selected
                </span>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onClearMultiSelection(row.key)}
                    className="rounded border border-gray-700 px-2 py-1 text-xs font-medium text-gray-300 transition-colors hover:border-gray-500 hover:bg-gray-700 hover:text-gray-100 focus-visible:border-gray-500 focus-visible:bg-gray-700 focus-visible:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300/40"
                  >
                    Clear Selection
                  </button>
                  <button
                    type="button"
                    disabled={allTargetsSelected}
                    onClick={() => onSelectAllTargets(row.key, row.targets)}
                    className="rounded border border-gray-700 px-2 py-1 text-xs font-medium text-gray-300 transition-colors hover:border-gray-500 hover:bg-gray-700 hover:text-gray-100 focus-visible:border-gray-500 focus-visible:bg-gray-700 focus-visible:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300/40 disabled:cursor-not-allowed disabled:border-gray-800 disabled:text-gray-600 disabled:hover:bg-transparent"
                  >
                    Select All
                  </button>
                </div>
              </div>
            ) : null}
            {row.targets.map((target, targetIndex) => (
              <SceneInventoryTargetRow
                key={target.id}
                row={row}
                target={target}
                targetIndex={targetIndex}
                activeTargetId={activeTargetId}
                selectedBodies={selectedBodies}
                selectedMeshIds={selectedMeshIds}
                getItemHidden={getItemHidden}
                onOpenTarget={onOpenTarget}
                onTargetRowKeyDown={onTargetRowKeyDown}
                onToggleItemVisibility={onToggleItemVisibility}
                onToggleMultiSelection={onToggleMultiSelection}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
